import React from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    Typography,
} from '@mui/material';
import FileUpload from '../shared/FileUpload';
import ImportProgressDialog from './accountImport/ImportProgressDialog';
import ImportResult from './accountImport/ImportResult';
import PreviewSections from './accountImport/PreviewSections';
import useAccountImport from './accountImport/useAccountImport';
import { buildSections } from './accountImport/accountImportSections';
import { formatCurrency } from './accountImport/accountImportUtils';

const AccountImportPanel = () => {
    const {
        selectedFile,
        previewSummary,
        selection,
        sectionData,
        sectionYears,
        yearData,
        yearLoading,
        yearErrors,
        expandedYears,
        sectionLoading,
        sectionErrors,
        previewWarnings,
        importWarnings,
        importResult,
        importError,
        loadingPreview,
        loadingImport,
        importProgress,
        resetState,
        handleFileSelect,
        handlePreview,
        handleImport,
        sectionCounts,
        totalSelected,
        getSectionSelectionMetrics,
        getSectionYearSelectionMetrics,
        getYearSelectionMetrics,
        getYearCount,
        isItemSelected,
        loadSection,
        loadSectionYears,
        loadYearItems,
        toggleSelection,
        toggleAll,
        setYearExpanded,
        handleYearToggle,
        setExpandedYears,
    } = useAccountImport();

    const baseSections = React.useMemo(() => buildSections(formatCurrency), []);
    const sections = React.useMemo(() => (
        baseSections.map((section) => ({
            ...section,
            count: sectionCounts?.[section.key] || 0,
        }))
    ), [baseSections, sectionCounts]);

    return (
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 28px rgba(0,0,0,0.06)' }}>
            <CardHeader
                title="Import"
                subheader="Legacy-Datenbank (.db/.sqlite/.sql) hochladen, Vorschau prüfen und gezielt importieren."
            />
            <CardContent>
                <ImportProgressDialog open={loadingImport} progress={importProgress} />
                <Stack spacing={2}>
                    <FileUpload
                        onFileSelect={handleFileSelect}
                        onPreview={handlePreview}
                        onImport={handleImport}
                        onReset={resetState}
                        accept=".db,.sqlite,.sqlite3,.sql"
                        loadingPreview={loadingPreview}
                        loadingImport={loadingImport}
                        selectedFile={selectedFile}
                        importDisabled={!previewSummary}
                    />

                    <Typography variant="body2" color="text.secondary">
                        Standardmaessig sind alle Datensaetze markiert. Profile koennen nur einzeln ausgewaehlt werden.
                    </Typography>

                    {importError && (
                        <Alert severity="error">{importError}</Alert>
                    )}

                    {previewWarnings.map((warning, index) => (
                        <Alert key={`preview-${index}`} severity="warning">
                            {warning}
                        </Alert>
                    ))}

                    {previewSummary && selection && (
                        <Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Vorschau (Auswahl: {totalSelected})
                            </Typography>
                            <PreviewSections
                                sections={sections}
                                selection={selection}
                                sectionData={sectionData}
                                sectionYears={sectionYears}
                                yearData={yearData}
                                yearLoading={yearLoading}
                                yearErrors={yearErrors}
                                expandedYears={expandedYears}
                                sectionLoading={sectionLoading}
                                sectionErrors={sectionErrors}
                                getSectionSelectionMetrics={getSectionSelectionMetrics}
                                getSectionYearSelectionMetrics={getSectionYearSelectionMetrics}
                                getYearSelectionMetrics={getYearSelectionMetrics}
                                getYearCount={getYearCount}
                                isItemSelected={isItemSelected}
                                toggleSelection={toggleSelection}
                                toggleAll={toggleAll}
                                loadSection={loadSection}
                                loadSectionYears={loadSectionYears}
                                loadYearItems={loadYearItems}
                                setYearExpanded={setYearExpanded}
                                handleYearToggle={handleYearToggle}
                                setExpandedYears={setExpandedYears}
                            />
                        </Box>
                    )}

                    {importWarnings.map((warning, index) => (
                        <Alert key={`import-${index}`} severity="warning">
                            {warning}
                        </Alert>
                    ))}

                    {importResult && (
                        <Box>
                            <Divider sx={{ my: 2 }} />
                            <ImportResult result={importResult} />
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default AccountImportPanel;
